import { alpha } from '@mui/material/styles';
import { border } from '@mui/system';

// ----------------------------------------------------------------------

function createGradient(color1: string, color2: string) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

interface GradientsPaletteOptions {
  primary: string;
  info: string;
  success: string;
  warning: string;
  error: string;
  gradient1: string;
  gradient2: string;
  gradient3: string;
  gradient4: string;
  statusInProgress: string;
  gradient5: string;
  gradient6: string;
  gradient7: string;
  gradient8: string;
  gradient9: string;

  cardGradient1: string;
  cardGradient2: string;
  cardGradient3: string;
  cardBorderGradient1: string;
  cardBorderGradient2: string;

  skeletonGradient: string;
}

interface ChartPaletteOptions {
  violet: string[];
  blue: string[];
  green: string[];
  yellow: string[];
  red: string[];
}

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
    darkbg: string;
    avatarCard: string;
    avatarCardInProgress: string;
    avatarCardFooter: string;
    sideDrawer: string;
    modal: string;
    menu: string;
    card: string;
    cardHover: string;
    dashboardSideDrawer: string;
    dark: string;
    videoStatus: string;
    videoStatusError: string;
    cardFooterHover: string;
    cardBorder: string;
    selectedCardBorder: string;
    divider: string;
    popover: string;
    dropdownGray: string;
    cancelBtn: string;
    note: string;
    uploaderBorder: string;
    inputBackground: string;
    brollModalGrey: string;
    lightGrey: string;
    deleteBtn: string;
    noteBox: string;
    creationBorder: string;
    primary: '#000';
    secondary: string;
    ternary: string;
    sidebarBorder: string;
    border: string;
    activeBorder: string;
    activeCard: string;
    errorCard: string;
    draftCard: string;
    skeleton: string;
    primaryV2: string;
    secondaryV2: string;
    ternaryV2: string;
    errorV2: string;
    borderV2: string;
    secondaryBorderV2: string;
    expressPrimaryV3: string;
    expressSecondaryV3: string;
    expressPanelV3: string;
    expressBorderV3: string;
    // Express Checkout additional reusable tokens (V3)
    expressRootV3: string; // overall page background
    expressCardV3: string; // card/panel base surface
    expressFieldV3: string; // input and muted surfaces
    expressAccentV3: string; // accent (step active circle)
    expressInactiveBgV3: string; // subtle inactive circle / chip background
    expressInactiveTextV3: string; // subdued text
    expressBorderSubtleV3: string; // faint borders/lines
    expressDividerV3: string; // divider line color
    expressCountdownTextV3: string; // countdown timer text
    primaryV3: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
  interface CommonColors {
    black: string;
    white: string;
    tertiary1: string;
    tertiary2: string;
  }
  interface Palette {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
  }
  interface PaletteOptions {
    gradients: GradientsPaletteOptions;
    chart: ChartPaletteOptions;
  }

  interface TypeText {
    // primary: string;
    cardText: string;
    secondary: string;
    disabled: string;
    modal: string;
    modalLightText: string;
    dateAndTime: string;
    dashboardMenu: string;
    cardFooterHover: string;
    coloredText: string;
    btnText: string;
    error: string;
    inputValue: string;
    whatsNew: string;
    placeholder: string;
    primary: string;
    whatsNewText: string;
    highlightedText: string;
    lighterText: string;
    grayText: string;
    loginPageHeadline: string;
    lighterText2: string;
  }
}

declare module '@mui/material' {
  interface Color {
    0: string;
    500_8: string;
    500_12: string;
    500_16: string;
    500_24: string;
    500_32: string;
    500_48: string;
    500_56: string;
    500_80: string;
  }
}

// SETUP COLORS
const PRIMARY = {
  lighter: '#C2A1F8',
  light: '#9E6BF3',
  main: '#7B35EF',
  dark: '#5C11D8',
  darker: '#450DA2',
};
const SECONDARY = {
  lighter: '#FEBFE4',
  light: '#FD81CA',
  main: '#FC42B0',
  dark: '#FA0496',
  darker: '#BC0370',
};
const INFO = {
  lighter: '#CEEFFF',
  light: '#8CDBFF',
  main: '#4AC6FF',
  dark: '#08B1FF',
  darker: '#0087C5',
};
const SUCCESS = {
  lighter: '#15D622',
  light: '#12B81D',
  main: '#0F9918',
  dark: '#0C7A13',
  darker: '#095C0E',
};
const WARNING = {
  lighter: '#FFB866',
  light: '#FFA033',
  main: '#FF8800',
  dark: '#CC6D00',
  darker: '#995200',
};
const ERROR = {
  lighter: '#F4AEAE',
  light: '#EC7979',
  main: '#E54545',
  dark: '#D11D1D',
  darker: '#9D1616',
};

const GREY = {
  0: '#000',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#56556C',
  600: '#637381',
  700: '#454F5B',
  800: '#242435',
  900: '#0B0B18',
  500_8: alpha('#56556C', 0.08),
  500_12: alpha('#56556C', 0.12),
  500_16: alpha('#56556C', 0.16),
  500_24: alpha('#56556C', 0.24),
  500_32: alpha('#56556C', 0.32),
  500_48: alpha('#56556C', 0.48),
  500_56: alpha('#56556C', 0.56),
  500_80: alpha('#56556C', 0.8),
  light: '#6A6A6A',
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
  gradient1: 'linear-gradient(149.97deg, #00EFFF 0.62%, #7B35EF 80.86%)',
  gradient2: 'linear-gradient(112.02deg, #0DB5FE -5.93%, #EF64FD 52.85%, #7B35EF 81.95%)',
  gradient3:
    'linear-gradient(149.36deg, rgba(0, 239, 255, 0.19) 0.63%, rgba(123, 53, 239, 0.09) 77.04%)',
  gradient4:
    'linear-gradient(93.82deg, #74B1F8 0.12%, #FF79D2 33.75%, #FFD58F 68.83%, #B1F777 102.74%)',
  statusInProgress: `linear-gradient(118deg, #0DB5FE, #EF64FD,#7B35EF)`,
  gradient5: 'linear-gradient(149.97deg, #00EFFF 0.62%, #7B35EF 80.86%)',
  gradient6: 'linear-gradient(149.97deg, #FFFFFF 30%, #D5B5FF 70%)',
  gradient7: 'linear-gradient(90deg, #1F1C31 10%, #000000 40%, #0C082A)',
  gradient8: 'linear-gradient(90deg, #E4626F 10%, #F23F51 40%, #FB283D)',
  gradient9: 'linear-gradient(90deg, #505050 10%, #919191 40%, #A6A6A6)',

  cardGradient1: 'linear-gradient(91.43deg, #1F1C31 -9.25%, #000000 -9.25%, #0C082A 110.21%)',
  cardGradient2: 'linear-gradient(91.43deg, #1F1C31 8.51%, #000000 60.72%, #0C082A 93.4%)',
  cardGradient3: 'linear-gradient(90deg, #081634 10.17%, #3F1375 123.5%)',
  cardBorderGradient1:
    'linear-gradient(99.3deg, rgba(64, 175, 247, 0.1) 7.24%, rgba(107, 97, 168, 0.1) 94.27%)',
  cardBorderGradient2: 'linear-gradient(99.3deg, #40AFF7 7.24%, #6B61A8 94.27%)',

  skeletonGradient: 'linear-gradient(90deg, transparent, rgba(86, 85, 108, 1), transparent)',
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2D99FF', '#83CFFF', '#A5F3FF', '#CCFAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
};

const COMMON = {
  common: { black: '#000', white: '#fff', tertiary1: '#15B1FB', tertiary2: '#E3D4FC' },
  primary: { ...PRIMARY, contrastText: '#000' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
    cardColor: '#242435',
  },
};

const palette = {
  light: {
    ...COMMON,
    mode: 'light',
    text: {
      // primary: '#050308',
      cardText: '#868686',
      secondary: '#606060',
      disabled: GREY[500],
      modal: '#1B0F33',
      modalLightText: GREY.light,
      dateAndTime: '#757575',
      dashboardMenu: '#624391',
      videoStatusText: '#FFFFFF',
      cardFooterHover: '#FEFEFE',
      coloredText: '#7B35EF',
      btnText: '#FFFFFF',
      error: '#FF0000',
      inputValue: '#757575',
      whatsNew: '#9C6BDE',
      // New style dark theme
      placeholder: '#706C8B',
      primary: '#fff',
      whatsNewText: '#D1B2FB',
      highlightedText: '#AF80FF',
      lighterText: '#8D8A9F',
      lighterText2: '#BFB1D6',
      grayText: '#B5B5B5',
      loginPageHeadline: '#B38BF4',
    },
    background: {
      paper: '#fff',
      default: '#fff',
      neutral: GREY[200],
      dummyColor: GREY[900],
      avatarCard: '#F8F8FF',
      avatarCardInProgress: '#F8F8FF',
      avatarCardFooter: '#F8F8FF',
      sideDrawer: '#F8F8FF',
      modal: '#F4F2F7',
      menu: '#E4E4E4',
      // border: '#000',
      input: '#F4F2F7',
      disabledInput: '#EAE6F0',
      chip: '#EAE6F0',
      card: '#F8F8FF',
      cardHover: '#EAEAFE',
      dark: '#757575',
      videoStatus: '#9E43F3',
      videoStatusError: '#FE43AF',
      cardFooterHover: '#8E1DFF',
      selectedCardBorder: '#7B35EF',
      cardBorder: '#EAEAFE',
      divider: '#2D2D3D',
      popover: '#fff',
      dropdownGray: '#ECEBEB',
      cancelBtn: '#757575',
      note: '#CEEFFF',
      uploaderBorder: '#BFC5EA',
      inputBackground: '#F9FAFB',
      brollModalGrey: '#E5E7EB',
      lightGrey: '#F3F4F6',
      deleteBtn: '#E4626F',
      noteBox: '#C3ECFF',
      creationBorder: '#D8D8FA',
      // New dark theme style
      primary: '#000',
      secondary: '#0C082A',
      ternary: '#403A6A',
      sidebarBorder: '#1A1444',
      border: '#403A6A',
      activeBorder: '#7B35EF',
      activeCard: '#2A2642',
      errorCard: 'rgba(228,98,111,0.2)',
      draftCard: '#292929',
      skeleton: '#42464b',
      // New dark theme V2
      primaryV2: '#282130',
      secondaryV2: '#151218',
      ternaryV2: '#473F50',
      errorV2: '#453131',
      borderV2: '#8C8C8C',
      secondaryBorderV2: '#848188',
      // Express Checkout V3 theme additions (aligned to design screenshot)
      // Use the project's selection palette: deep navy background, muted panel, and purple accents
      expressPrimaryV3: '#0C082A', // page background / deep navy
      expressSecondaryV3: '#282130', // secondary background tone used around panels
      expressPanelV3: '#282130', // main panel surface
      expressBorderV3: '#403A6A', // subtle purple border for panels
      // Extended Express Checkout palette (postfix V3 for reusability)
      expressRootV3: '#090817',
      expressCardV3: '#282130',
      expressFieldV3: '#473F50',
      expressAccentV3: '#6A35FF',
      expressInactiveBgV3: 'rgba(255,255,255,0.08)',
      expressInactiveTextV3: 'rgba(255,255,255,0.5)',
      expressBorderSubtleV3: 'rgba(255,255,255,0.15)',
      expressDividerV3: 'rgba(255,255,255,0.06)',
      expressCountdownTextV3: '#AAAAAA',
      primaryV3: '#1F1D27',
    },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: '#fff',
      cardText: '#9C9C9C',
      secondary: GREY[500],
      disabled: GREY[600],
      modal: '#1B0F33',
      modalLightText: GREY.light,
      dashboardMenu: '#624391',
    },
    background: {
      paper: GREY[800],
      default: GREY[900],
      neutral: GREY[500_16],
      dummyColor: GREY[900],
      avatarCard: GREY[800],
      avatarCardFooter: GREY[900],
      modal: GREY[900],
      menu: GREY[900],
      card: '#F8F8FF',
      cardHover: '#EAEAFE',
      sideDrawer: '#F8F8FF',
      dark: '#757575',
      popover: '#fff',
      dropdownGray: '#ECEBEB',
      cancelBtn: '#757575',
    },
    action: { active: GREY[500], ...COMMON.action },
  },
} as const;

export default palette;
